import { useEffect, useRef, useState } from 'react'
import { useEditor } from '@tiptap/react'
import type { AnyExtension, Editor } from '@tiptap/core'
import type { Doc as YDoc } from 'yjs'
import { mergeAttributes, Node } from '@tiptap/core'

import { ExtensionKit } from '@/tiptap/extensions/extension-kit'
import { useOrder } from '@/contexts/OrderContext'

declare global {
  interface Window {
    editor: Editor | null
  }
}

export const useBlockEditor = ({
  aiToken,
  content,
  onContentChange,
  readOnly,
  ydoc,
  provider,
  enableBold,
  userId,
  userName = 'Maxi',
}: {
  aiToken?: string
  ydoc: YDoc
  provider?: null | undefined
  userId?: string
  userName?: string
}) => {
  const editorInitialized = useRef(false);
  const editorInitialized2 = useRef(false);

  const { setExamTablesEditor } = useOrder()

  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      autofocus: false,
      editable: !readOnly,
      onUpdate: ctx => {
        if (editorInitialized2.current) {
          onContentChange(ctx.editor?.getHTML());
        } else if (!editorInitialized.current) {
          editorInitialized.current = true;
        } else {
          editorInitialized2.current = true;
        }
      },
      onCreate: ctx => {        
        editor.view.dom.setAttribute("spellcheck", "false");
        editor.view.dom.setAttribute("autocomplete", "off");
        editor.view.dom.setAttribute("autocapitalize", "off");

        ctx.editor.setExamTablesEditor = setExamTablesEditor;
      },
      content,
      extensions: [
        ...ExtensionKit({ provider, enableBold }),
        // CustomElement
      ].filter((e): e is AnyExtension => e !== undefined),
      editorProps: {
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          class: 'min-h-full',
        },
      },
    },
    [ydoc, provider],
  )

  window.editor = editor

  return { editor }
}


const blockTags = [
  "address", "article", "aside", "blockquote", "canvas", "dd", "div", "dl", "dt",
  "fieldset", "figcaption", "figure", "footer", "form", "h1", "h2", "h3", "h4",
  "h5", "h6", "header", "hr", "li", "main", "nav", "noscript", "ol", "output",
  "p", "pre", "section", "tfoot", "ul", "video", "caption", "col", "colgroup",
  "tbody", "td", "tfoot", "th", "thead", "tr", "br"
]

const inlineTags = [
  "a", "abbr", "acronym", "audio", "b", "bdi", "bdo", "big", "cite", "code", "data",
  "datalist", "del", "dfn", "em", "i", "ins", "kbd", "map", "mark", "noscript",
  "output", "picture", "q", "ruby", "s", "samp", "script", "slot", "small", "span",
  "strong", "sub", "sup", "svg", "template", "time", "tt", "u", "var", "video",
  "wbr", "iframe", "source", "track"
]

const inlineBlockTags = [
  "input", "textarea", "button", "select", "label", "progress", "meter",
  "embed", "object"
]

const attributes = [
  "style", "align", "width", "height", "border", "cellspacing",
  "alt", "src", "srcset", "href", "rel", "target"
]

const CustomElement = Node.create({
  name: "attributesPreserver",
  defining: true,

  addOptions() {
    const options = { tagname: null }
    attributes.forEach(attribute => {
      options[attribute] = null
    })
    return options
  },

  // Define the group – here, we let <p> and <li> be block nodes (for layout) but we will control their content.
  group() {
    const { tagname } = this.options
    if (tagname === "p" || tagname === "li") return "block"
    if (inlineTags.includes(tagname)) return "inline"
    if (blockTags.includes(tagname)) return "block"
    if (inlineBlockTags.includes(tagname)) return "inline"
    return "block"
  },

  // Control the allowed content: for <p> and <li>, allow only inline nodes (so no extra wrapping <p> is inserted).
  content() {
    const { tagname } = this.options
    if (tagname === "p" || tagname === "li") return "inline*"
    if (inlineTags.includes(tagname)) return "inline*"
    if (blockTags.includes(tagname)) return "block*"
    if (inlineBlockTags.includes(tagname)) return "inline*"
    return "block*" // Default to block content
  },

  addAttributes() {
    const attributesSet = { tagname: { default: null } }
    attributes.forEach(attribute => {
      attributesSet[attribute] = { default: null }
    })
    return attributesSet
  },

  parseHTML() {
    // Build a parser for all the tags you want to support.
    return [
      ...blockTags.map(tag => ({
        tag,
        priority: 1000,
        getAttrs: dom => {
          const attributesSet = {
            tagname: dom.nodeName.toLowerCase() || this.options.tagname
          }
          attributes.forEach(attribute => {
            attributesSet[attribute] = dom.getAttribute(attribute) || this.options[attribute]
          })
          return attributesSet
        }
      })),
      ...inlineTags.map(tag => ({
        tag,
        priority: 1000,
        getAttrs: dom => {
          const attributesSet = {
            tagname: dom.nodeName.toLowerCase() || this.options.tagname
          }
          attributes.forEach(attribute => {
            attributesSet[attribute] = dom.getAttribute(attribute) || this.options[attribute]
          })
          return attributesSet
        }
      })),
      ...inlineBlockTags.map(tag => ({
        tag,
        priority: 1000,
        getAttrs: dom => {
          const attributesSet = {
            tagname: dom.nodeName.toLowerCase() || this.options.tagname
          }
          attributes.forEach(attribute => {
            attributesSet[attribute] = dom.getAttribute(attribute) || this.options[attribute]
          })
          return attributesSet
        }
      }))
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    // Remove the internal 'tagname' attribute from the output.
    const { tagname, ...attrs } = HTMLAttributes
    return [
      node.attrs.tagname, // Use the internal tagname as the actual HTML tag.
      mergeAttributes(this.options.HTMLAttributes, attrs),
      0
    ]
  }
})